import PropTypes from 'prop-types';
import { useEffect, useReducer } from 'react';
import * as Sentry from '@sentry/react';
// utils
import { isEmpty } from 'lodash';
import { getParsedData, localStorageGetItem, localStorageRemoveItem } from '../utils/storage-available';
import axios from '../utils/axios';
import { callApi } from '../utils/apiCall';
import { isValidToken, setSession } from '../utils/jwt';
import { dispatch as reduxDispatch } from '../redux/store';
import { updatePracticeId } from '../redux/slices/practiceTreatmentList';
import { setReminderData, setToDoData } from '../redux/slices/toDoNotifications';
import { setEmailNotifications, setWpNotifications } from '../redux/slices/liveNotificationSlice';
import { fetchMyPermissions, clearPermissions } from '../redux/slices/permissions';
import { clearAllDrafts } from '../utils/callScriptDraftDB';
import { clearAllRecentFeedbackSends } from '../utils/recentFeedbackSends';
import { AuthContext } from './AuthContext';

// ----------------------------------------------------------------------

const initialState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
};

const handlers = {
    INITIALIZE: (state, action) => {
        const { isAuthenticated, user } = action.payload;
        return {
            ...state,
            isAuthenticated,
            isInitialized: true,
            user,
        };
    },
    LOGIN: (state, action) => {
        const { user } = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            user,
        };
    },
    LOGOUT: (state) => ({
        ...state,
        isAuthenticated: false,
        user: null,
    }),
    REGISTER: (state, action) => {
        const { user } = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            user,
        };
    }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        const initialize = async () => {
            try {
                let accessToken = localStorage.getItem('accessToken');
                const refreshToken = localStorage.getItem('refreshToken');

                if (accessToken && isValidToken(refreshToken)) {
                    // If access token is expired but refresh is valid, proactively refresh
                    // to avoid a 401 round-trip on the /auth/user/ call
                    if (!isValidToken(accessToken)) {
                        try {
                            const { default: plainAxios } = await import('axios');
                            const { HOST_API } = await import('../config');
                            const refreshResp = await plainAxios.post(
                                `${HOST_API}/api/v1/auth/token/refresh/`,
                                { refresh: refreshToken }
                            );
                            if (refreshResp.status === 200) {
                                accessToken = refreshResp.data.access;
                            }
                        } catch {
                            // Refresh failed — fall through to unauthenticated state.
                            // Clear every per-user practice selection so a
                            // new login on the same tab doesn't carry the
                            // previous user's `practice__in` selection into
                            // their first requests (would produce 403s or
                            // — on overlap — silently wrong aggregations).
                            reduxDispatch(updatePracticeId(null));
                            localStorage.removeItem('practiceId');
                            localStorage.removeItem('activePracticeIds');
                            localStorage.removeItem('reportPracticeIds');
                            dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
                            return;
                        }
                    }
                    setSession(accessToken, refreshToken);

                    const response = await callApi('/api/v1/auth/user/');
                    if (response.status === 200) {
                        const user = response.data;
                        const practiceId = localStorage.getItem('practiceId');
                        if (practiceId) {
                            await reduxDispatch(fetchMyPermissions(practiceId));
                        }
                        dispatch({
                            type: 'INITIALIZE',
                            payload: {
                                isAuthenticated: true,
                                user,
                            },
                        });
                        return;
                    }
                    dispatch({
                        type: 'INITIALIZE',
                        payload: {
                            isAuthenticated: false,
                            user: null,
                        },
                    });
                } else {
                    dispatch({
                        type: 'INITIALIZE',
                        payload: {
                            isAuthenticated: false,
                            user: null,
                        },
                    });
                }
            } catch (err) {
                // Report to Sentry — this catch was previously silent, making
                // auth initialization failures invisible (corrupted token, API
                // unreachable on load, malformed response).
                Sentry.captureException(err, { tags: { feature: 'auth-initialize' } });
                reduxDispatch(updatePracticeId(null));
                // Same cross-user-leak guard as the refresh-failed catch
                // above — clear every per-user practice selection so the
                // next login starts with a clean slate.
                localStorage.removeItem('practiceId');
                localStorage.removeItem('activePracticeIds');
                localStorage.removeItem('reportPracticeIds');
                // Reload guard: prevent infinite reload loop if error is persistent
                // (e.g., backend down). Same pattern as app.jsx hook_error_reload.
                const reloadKey = 'auth_init_reload';
                if (!sessionStorage.getItem(reloadKey)) {
                    sessionStorage.setItem(reloadKey, '1');
                    window.location.reload();
                }
                dispatch({
                    type: 'INITIALIZE',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                });
            }
        };

        initialize();
    }, []);

    const login = async (email, password) => {
        const response = await axios.post('/api/v1/auth/login/?format=json', {
            email,
            password,
        });
        // eslint-disable-next-line camelcase
        const { access_token, user, refresh_token } = response.data;
    
        setSession(access_token, refresh_token);

        const practiceId = localStorage.getItem('practiceId');
        if (practiceId) {
            await reduxDispatch(fetchMyPermissions(practiceId));
        }

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const register = async (data) => {
        await axios.post('/api/v1/register/', data);
    };

    const confirmemail = async (key, emailVerification) => {
        try {
            const response = await axios.post('/api/v1/register/account-confirm-email/', key);
            if(response.status === 200) {
                emailVerification({ message: 'Email confirm successfully' });
            }
        } catch (error) {
            emailVerification({ error: error.detail });
        }
    };

    const updateCurrentUser = async (user) => {
        dispatch({
            type: 'INITIALIZE',
            payload: {
                isAuthenticated: true,
                user,
            },
        });
    };

    const logout = async () => {
        setSession(null, null);
        reduxDispatch(updatePracticeId(null));
        localStorageRemoveItem('practiceId');
        reduxDispatch(setToDoData([]));
        reduxDispatch(setEmailNotifications([]));
        reduxDispatch(setWpNotifications([]));
        reduxDispatch(setReminderData([]));
        reduxDispatch(clearPermissions());
        const intervalId = localStorageGetItem('intervalId');
        const timeoutId = localStorageGetItem('timeoutId');
        const timeoutIdV2 = localStorageGetItem('intervalIdV2');
        const snoozedData = getParsedData('timeout_ids');
        if (!isEmpty(snoozedData)) {
            snoozedData?.forEach((element) => {
                clearTimeout(element);
            });
        }
        clearInterval(intervalId);
        clearInterval(timeoutIdV2);
        clearTimeout(timeoutId);
        localStorageRemoveItem('intervalStartTime');
        localStorageRemoveItem('intervalId');
        localStorageRemoveItem('timeoutId');
        localStorageRemoveItem('intervalIdV2');
        localStorageRemoveItem('reminderData');
        localStorageRemoveItem('timeout_ids');
        // Clear call script drafts from IndexedDB on logout (data compliance)
        clearAllDrafts();
        // Clear Patient Feedback recent-send sessionStorage so a subsequent
        // user on the same tab doesn't see the prior user's send activity.
        clearAllRecentFeedbackSends();
        dispatch({ type: 'LOGOUT' });
    };

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'jwt',
                login,
                logout,
                register,
                confirmemail,
                updateCurrentUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

AuthProvider.propTypes = {
    children: PropTypes.node,
};

export { AuthProvider };
